import React from "react";
import { useQuery } from "@tanstack/react-query";
import { generateClient } from "aws-amplify/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Trophy, Medal, TrendingUp } from "lucide-react";
import {
  listPlayerConstructs,
  listEventPlayerResults,
  listEvents,
} from "../../graphql/queries";

const client = generateClient();

const LeagueLeaderboard: React.FC = () => {
  // Fetch all data needed for leaderboard
  const { data: playerConstructs, isLoading: constructsLoading } = useQuery({
    queryKey: ["leaderboardPlayerConstructs"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listPlayerConstructs,
        variables: { limit: 1000 },
      });
      return result.data.listPlayerConstructs.items;
    },
  });

  const { data: eventPlayerResults, isLoading: resultsLoading } = useQuery({
    queryKey: ["leaderboardEventPlayerResults"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listEventPlayerResults,
        variables: { limit: 1000 },
      });
      return result.data.listEventPlayerResults.items;
    },
  });

  const { data: events, isLoading: eventsLoading } = useQuery({
    queryKey: ["leaderboardEvents"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listEvents,
        variables: { limit: 1000 },
      });
      return result.data.listEvents.items;
    },
  });

  const isLoading = constructsLoading || resultsLoading || eventsLoading;

  // Calculate current league standings
  const leaderboard = React.useMemo(() => {
    if (!playerConstructs || !eventPlayerResults || !events) {
      return [];
    }

    return playerConstructs
      .map((construct: any) => {
        // Get this construct's event results
        const constructEventResults = eventPlayerResults.filter(
          (result: any) => result.playerConstructId === construct.id
        );

        // Get events for this construct
        const constructEvents = constructEventResults
          .map((result: any) => {
            const event = events.find((e: any) => e.id === result.eventId);
            return {
              ...result,
              event,
            };
          })
          .filter((item: any) => item.event);

        // Calculate current season stats
        const totalEvents = constructEvents.length;
        const totalPoints = constructEvents.reduce(
          (sum: number, event: any) => sum + (event.points || 0),
          0
        );
        const bestRank =
          constructEvents.length > 0
            ? Math.min(...constructEvents.map((e: any) => e.rank || 999))
            : null;
        const averageRank =
          constructEvents.length > 0
            ? constructEvents.reduce(
                (sum: number, event: any) => sum + (event.rank || 0),
                0
              ) / constructEvents.length
            : null;

        // Calculate match record
        const totalWins = constructEvents.reduce(
          (sum: number, event: any) => sum + (event.wins || 0),
          0
        );
        const totalLosses = constructEvents.reduce(
          (sum: number, event: any) => sum + (event.losses || 0),
          0
        );
        const totalDraws = constructEvents.reduce(
          (sum: number, event: any) => sum + (event.draws || 0),
          0
        );
        const totalMatches = totalWins + totalLosses + totalDraws;
        const matchWinPercentage =
          totalMatches > 0 ? (totalWins / totalMatches) * 100 : 0;

        return {
          id: construct.id,
          name: construct.name,
          totalEvents,
          totalPoints,
          bestRank,
          averageRank,
          totalWins,
          totalLosses,
          totalDraws,
          totalMatches,
          matchWinPercentage,
          record: `${totalWins}-${totalLosses}-${totalDraws}`,
        };
      })
      .filter((player: any) => player.totalEvents > 0) // Only show players with events
      .sort((a: any, b: any) => {
        // Sort by total points (descending), then by average rank (ascending)
        if (b.totalPoints !== a.totalPoints) {
          return b.totalPoints - a.totalPoints;
        }
        if (a.averageRank && b.averageRank) {
          return a.averageRank - b.averageRank;
        }
        return 0;
      });
  }, [playerConstructs, eventPlayerResults, events]);

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-lg">Loading league standings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex items-center gap-3 mb-6">
        <Trophy className="h-8 w-8 text-primary" />
        <h1 className="text-3xl font-bold">Current League Leaderboard</h1>
      </div>

      {leaderboard.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <Trophy className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No league data found</p>
              <p className="text-sm text-muted-foreground mt-2">
                Import some EventLink data to see the leaderboard
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {leaderboard.map((player: any, index: number) => (
            <Card
              key={player.id}
              className={index < 3 ? "border-primary/20" : ""}
            >
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div
                      className={`flex items-center justify-center w-10 h-10 rounded-full font-bold text-lg ${
                        index === 0
                          ? "bg-yellow-100 text-yellow-800"
                          : index === 1
                            ? "bg-gray-100 text-gray-800"
                            : index === 2
                              ? "bg-orange-100 text-orange-800"
                              : "bg-primary text-primary-foreground"
                      }`}
                    >
                      {index + 1}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">{player.name}</h3>
                      <p className="text-sm text-muted-foreground">
                        {player.totalEvents} events • {player.record} record
                      </p>
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-2xl font-bold text-primary">
                      {player.totalPoints}
                    </div>
                    <div className="text-sm text-muted-foreground">points</div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t">
                  <div className="text-center">
                    <div className="text-lg font-semibold">
                      {player.bestRank ? `#${player.bestRank}` : "N/A"}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Best Rank
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold">
                      {player.averageRank
                        ? player.averageRank.toFixed(1)
                        : "N/A"}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Avg Rank
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold">
                      {player.matchWinPercentage.toFixed(1)}%
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Match Win %
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            League Stats
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">
                {leaderboard.length}
              </div>
              <div className="text-sm text-muted-foreground">
                Active Players
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">
                {events?.length || 0}
              </div>
              <div className="text-sm text-muted-foreground">Total Events</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">
                {eventPlayerResults?.length || 0}
              </div>
              <div className="text-sm text-muted-foreground">Event Results</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">
                {leaderboard.length > 0 ? leaderboard[0].totalPoints : 0}
              </div>
              <div className="text-sm text-muted-foreground">Leader Points</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LeagueLeaderboard;

